import { downloadBlob } from "../../lib/download-blob";
import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";
import { writePsd, type Layer } from "ag-psd";
import { artRgbColor } from "../../lib/schedule-art/art-color";
import { artFontWeight, loadArtFonts } from "../../lib/schedule-art/art-fonts";
import Konva from "konva";
import type { CustomArt } from "../../stores/custom-art-store";
import { loadArtImage, scheduleDayArtSources } from "../../lib/schedule-art/art-assets";
import { renderArtText, type ArtData } from "./art-text";
import { psdTextStroke } from "./psd-text-stroke";
import { loadPlatformImages } from "../../lib/schedule-art/platform-images";
import { ART_HEIGHT, ART_WIDTH, createArtExportStage, rasterizeArtNode } from "../../lib/schedule-art/art-canvas";
import { artSlotFields } from "../../lib/schedule-presets/art-fields";

export type PsdExportOptions = {
  includeTextLayers: boolean;
  hideImageFolders: boolean;
  includeCustomArt: boolean;
};

type GroupBuckets = { img: Layer[]; renderedText: Layer[]; text: Layer[] };
const weekdayNames = weekdayKeys.map((_, index) => String(index + 1).padStart(2, "0"));
const weekdayNameByKey = new Map<string, string>(weekdayKeys.map((key, index) => [key, weekdayNames[index]!]));
type LayerGroup = "stream" | "time" | "week" | "art";

const LAYER_GROUP_RULES: ReadonlyArray<readonly [RegExp, LayerGroup]> = [
  [/^offline\.message$/, "stream"],
  [/^(?:week\.range|day\.date)$/, "week"],
];

export function layerGroupForBind(bind: string): LayerGroup {
  const slotField = artSlotFields.find(field => bind.endsWith(`.${field.key}`));
  if (slotField) return slotField.layerGroup;
  const match = LAYER_GROUP_RULES.find(([pattern]) => pattern.test(bind));
  return match ? match[1] : "art";
}

function toPsdColor(color: string) {
  const rgb = artRgbColor(color);
  if (!rgb) return undefined;
  return { r: rgb.r, g: rgb.g, b: rgb.b };
}

function rasterLayer(stage: Konva.Stage, node: Konva.Group | Konva.Shape, name: string, padding = 0): Layer {
  return { name, ...rasterizeArtNode(stage, node, padding) };
}

function textBaselineOffset(text: Konva.Text): number {
  const context = document.createElement("canvas").getContext("2d")!;
  context.font = `${text.fontStyle()} ${text.fontSize()}px ${text.fontFamily()}`;
  const metrics = context.measureText("M");
  const ascent = metrics.fontBoundingBoxAscent ?? metrics.actualBoundingBoxAscent ?? text.fontSize() * 0.8;
  const descent = metrics.fontBoundingBoxDescent ?? metrics.actualBoundingBoxDescent ?? text.fontSize() * 0.2;
  const lineHeightPx = text.lineHeight() * text.fontSize();
  return (ascent - descent) / 2 + lineHeightPx / 2;
}

function postscriptFontName(family: string, weight: number): string {
  const compactFamily = family.replace(/\s+/g, "");
  const styleSuffix = weight >= 700 ? "Bold" : "Regular";
  return `${compactFamily}-${styleSuffix}`;
}

function editableTextLayer(stage: Konva.Stage, item: Konva.Group, text: Konva.Text, name: string): Layer {
  const fill = text.fill();
  const weight = artFontWeight(text.fontStyle());
  const fontName = postscriptFontName(text.fontFamily(), weight);
  // PSD text transforms use document coordinates with an alphabetic baseline.
  const transform = text.getAbsoluteTransform().copy();
  transform.translate(0, textBaselineOffset(text));
  const matrix = transform.getMatrix();
  const padding = text.fontSize() * 0.3;
  const stroke = psdTextStroke(text);
  // Keep the positioned group intact, just like the pre-rendered image layer.
  const raster = editableTextRaster(stage, item, padding, !!stroke.effects);
  const textProperty = {
    text: text.text(),
    transform: [matrix[0]!, matrix[1]!, matrix[2]!, matrix[3]!, matrix[4]!, matrix[5]!],
    style: {
      font: {
        name: fontName,
      },
      fontSize: text.fontSize(),
      fillColor: typeof fill === "string" ? toPsdColor(fill) : undefined,
      ...stroke.style,
    },
  };
  return { name, text: textProperty, effects: stroke.effects, ...raster };
}

function editableTextRaster(
  stage: Konva.Stage,
  item: Konva.Group,
  padding: number,
  hasStrokeEffect: boolean,
) {
  if (!hasStrokeEffect) return rasterizeArtNode(stage, item, padding);

  // Layer effects apply to the cached pixels too; cache the fill only.
  const fillOnly = item.clone();
  try {
    fillOnly.findOne<Konva.Text>("Text")!.strokeEnabled(false);
    return rasterizeArtNode(stage, fillOnly, padding);
  } finally {
    fillOnly.destroy();
  }
}

export async function exportSchedulePsd(
  data: ArtData,
  filename: string,
  layers: CustomArt[],
  offlineLabel: string,
  options: PsdExportOptions,
) {
  const daySources = scheduleDayArtSources(data.preset, data.week, data.slots);
  const sources = [data.preset.base, ...daySources];
  const [images] = await Promise.all([
    Promise.all(sources.map((source) => source ? loadArtImage(source) : undefined)),
    loadArtFonts(data.preset.metadata),
    loadPlatformImages(),
  ]);
  const [baseImage, ...dayImages] = images;
  const stage = createArtExportStage();
  try {
    const textLayer = new Konva.Layer({ listening: false });
    stage.add(textLayer);
    const textGroup = new Konva.Group();
    textLayer.add(textGroup);
    renderArtText(textGroup, data, offlineLabel);
    const items = [...textGroup.getChildren()] as Konva.Group[];

    const groups: Record<LayerGroup, GroupBuckets> = {
      stream: { img: [], renderedText: [], text: [] },
      time: { img: [], renderedText: [], text: [] },
      week: { img: [], renderedText: [], text: [] },
      art: { img: [], renderedText: [], text: [] },
    };

    if (options.includeCustomArt) {
      layers.filter(art => art.visible).forEach(art => {
        const artNode = new Konva.Image({ image: art.image, ...art.transform });
        groups.art.img.push(rasterLayer(stage, artNode, art.name));
      });
    }

    const baseNode = new Konva.Image({ image: baseImage!, x: 0, y: 0, width: ART_WIDTH, height: ART_HEIGHT });
    groups.art.img.push(rasterLayer(stage, baseNode, "Preset Base"));
    const weekdayIndex = new Map<string, number>(weekdayKeys.map((key, index) => [key, index]));
    for (const item of items) {
      const dayKey = item.getAttr("metadataDay") as string | undefined;
      const bind = (item.getAttr("metadataBind") as string | undefined) ?? "Element";
      const text = item.findOne<Konva.Text>("Text");
      const label = dayKey ? `${weekdayNameByKey.get(dayKey)} - ${bind}` : bind;
      const bucket = groups[layerGroupForBind(bind)];
      const textPadding = text ? text.fontSize() * 0.3 : 0;
      const raster = rasterLayer(stage, item, label, textPadding);
      if (text) bucket.renderedText.push(raster);
      else bucket.img.push(raster);
      if (text && options.includeTextLayers) bucket.text.push(editableTextLayer(stage, item, text, label));
    }

    const backgroundLayers: Layer[] = [...weekdayKeys].reverse().flatMap((dayKey) => {
      const index = weekdayIndex.get(dayKey)!;
      const image = dayImages[index];
      if (!image) return [];
      const node = new Konva.Image({ image, x: 0, y: 0, width: ART_WIDTH, height: ART_HEIGHT });
      return [rasterLayer(stage, node, `${weekdayNames[index]} Background`)];
    });

    const groupFolder = (name: LayerGroup): Layer => {
      const hideRenderedText = options.includeTextLayers && options.hideImageFolders;
      const children: Layer[] = [];
      if (groups[name].img.length) {
        children.push({ name: "img", children: groups[name].img });
      }
      if (groups[name].renderedText.length) {
        children.push({ name: "rendered text", hidden: hideRenderedText, children: groups[name].renderedText });
      }
      if (groups[name].text.length) {
        children.push({ name: "text", hidden: !options.hideImageFolders, children: groups[name].text });
      }
      return { name, children };
    };

    const children: Layer[] = [
      groupFolder("art"),
      { name: "backgrounds", children: backgroundLayers },
      groupFolder("week"),
      groupFolder("time"),
      groupFolder("stream"),
    ];

    const buffer = writePsd({ width: ART_WIDTH, height: ART_HEIGHT, children });
    const blob = new Blob([buffer], { type: "image/vnd.adobe.photoshop" });
    downloadBlob(blob, filename);
  } finally {
    stage.destroy();
  }
}
